import { useMutation } from "@apollo/client";
import { useCallback } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import { Edit } from "components/icons/generated";
import Markdown from "components/markdown/Markdown";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import LinkButton from "ds/components/Button/LinkButton";
import ButtonIcon from "ds/components/ButtonIcon";
import CardCollapsibleContent from "ds/components/Card/Collapsible/components/Content";
import CardCollapsibleToggle from "ds/components/Card/Collapsible/components/Toggle";
import CardCollapsibleWrapper from "ds/components/Card/Collapsible/components/Wrapper";
import CardHeader from "ds/components/Card/components/Header";
import CardTitle from "ds/components/Card/components/Title";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import Tooltip from "ds/components/Tooltip";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { Action, TerraformProviderVersionStatus } from "types/generated";
import { checkAllowedAction } from "utils/access";

import ProviderVersionArtifacts from "../ProviderVersionArtifacts";
import { showProviderVersionDrawer } from "../ProviderVersionDrawer";
import { showProviderVersionEditDrawer } from "../ProviderVersionEditDrawer";
import TerraformProviderVersionVerificationStatusBadge from "../ProviderVersionVerificationStatusBadge";
import TerraformProviderVersionStatusBadge from "../ProvidersVersionStatusBadge";
import {
  DELETE_PROVIDER_VERSION,
  PUBLISH_PROVIDER_VERSION,
  REVOKE_PROVIDER_VERSION,
  VERIFY_PROVIDER_VERSION,
} from "../gql";
import { showProviderVersionDeleteConfirmation } from "./DeleteConfirmation";
import styles from "./styles.module.css";
import {
  DeleteProviderVersionGql,
  ProviderVersionProps,
  PublishProviderVersionGql,
  RevokeProviderVersionGql,
  VerifyProviderVersionGql,
} from "./types";

function ProviderVersion({ provider, version }: ProviderVersionProps) {
  const [isCollapsed, toggle] = useToggle(true);
  const { onError, reportSuccess, reportError } = useTypedContext(FlashContext);
  const canDeleteProviderVersion = checkAllowedAction(version, Action.TerraformProviderVersionDelete);
  const canPublishProviderVersion = checkAllowedAction(version, Action.TerraformProviderVersionPublish);
  const canRevokeProviderVersion = checkAllowedAction(version, Action.TerraformProviderVersionRevoke);
  const canUpdateProviderVersion = checkAllowedAction(version, Action.TerraformProviderVersionUpdate);

  const [publishVersion, publishMutation] = useMutation<PublishProviderVersionGql>(
    PUBLISH_PROVIDER_VERSION,
    {
      refetchQueries: ["GetProviderWithVersions"],
    }
  );

  const [revokeVersion] = useMutation<RevokeProviderVersionGql>(REVOKE_PROVIDER_VERSION, {
    refetchQueries: ["GetProviderWithVersions"],
  });

  const [deleteVersion] = useMutation<DeleteProviderVersionGql>(DELETE_PROVIDER_VERSION, {
    refetchQueries: ["GetProviderWithVersions"],
  });

  const [verifyVersion, verifyMutation] =
    useMutation<VerifyProviderVersionGql>(VERIFY_PROVIDER_VERSION);

  const handlePublishProviderVersion = () => {
    publishVersion({
      variables: {
        version: version.id,
      },
    })
      .then(({ data }) => {
        if (data?.terraformProviderVersionPublish?.number) {
          reportSuccess({
            message: `Terraform provider version "${data.terraformProviderVersionPublish.number}" was successfully published`,
          });
        } else {
          reportError({
            message:
              "Something went wrong while publishing Terraform provider version, please try again.",
          });
        }
      })
      .catch(onError);
  };

  const handleDeleteProviderVersion = () => {
    deleteVersion({ variables: { version: version.id } })
      .then(({ data }) => {
        if (data?.terraformProviderVersionDelete?.deleted) {
          reportSuccess({
            message: `Terraform provider version "${data.terraformProviderVersionDelete.number}" was successfully deleted`,
          });
        } else {
          reportError({
            message:
              "Something went wrong while deleting Terraform provider version, please try again.",
          });
        }
      })
      .catch(onError);
  };

  const handleVerifyProviderVersion = () => {
    verifyVersion({ variables: { version: version.id } })
      .then(({ data }) => {
        const verified = data?.terraformProviderVersionVerify;
        if (verified?.verifiedAt && !verified.verificationFailure) {
          reportSuccess({
            message: `Terraform provider version "${verified.number}" was successfully verified`,
          });
        } else if (verified?.verificationFailure) {
          reportError({
            message: `Failed to verify Terraform provider version "${verified.number}": ${verified.verificationFailure}`,
          });
        } else {
          reportError({
            message:
              "Something went wrong while verifying Terraform provider version, please try again.",
          });
        }
      })
      .catch(onError);
  };

  const handleRevokeProviderVersion = () => {
    revokeVersion({ variables: { version: version.id } })
      .then(({ data }) => {
        if (
          data?.terraformProviderVersionRevoke?.status === TerraformProviderVersionStatus.Revoked
        ) {
          reportSuccess({
            message: `Terraform provider version "${data.terraformProviderVersionRevoke.number}" was successfully revoked`,
          });
        } else {
          reportError({
            message:
              "Something went wrong while revoking Terraform provider version, please try again.",
          });
        }
      })
      .catch(onError);
  };

  const isDraft = version.status === TerraformProviderVersionStatus.Draft;
  const isPublished = version.status === TerraformProviderVersionStatus.Published;
  const isRevoked = version.status === TerraformProviderVersionStatus.Revoked;

  // A version is considered verified only after a successful verification (no
  // failure). A previously failed verification stays retryable, so we keep the
  // action available in that case.
  const isVerified = !!version.verifiedAt && !version.verificationFailure;

  const revokeConfirmationModalShow = () => {
    showProviderVersionDeleteConfirmation({
      onConfirm: handleRevokeProviderVersion,
      isPublished,
      versionNumber: version.number,
    });
  };

  const deleteConfirmationModalShow = () => {
    showProviderVersionDeleteConfirmation({
      onConfirm: handleDeleteProviderVersion,
      isPublished,
      versionNumber: version.number,
    });
  };

  const handleToggle = useCallback(() => {
    toggle();
  }, [toggle]);

  return (
    <CardCollapsibleWrapper isCollapsed={isCollapsed} onToggle={handleToggle} role="listitem">
      <CardHeader>
        <Box
          gap="medium"
          grid
          gridTemplate="auto 1fr auto"
          gridAreas={`"indicator content actions" ${
            version.description ? `". description description"` : ""
          }`}
          className={version.description ? styles.headerWithDescription : undefined}
          fullWidth
        >
          <Box align="center" gridArea="indicator" gap="medium">
            <CardCollapsibleToggle ariaLevel={3} ariaLabel={version.number} />
            <CardTitle tag="span" role="heading" aria-level={2}>
              {version.number}
            </CardTitle>
            <TerraformProviderVersionStatusBadge status={version.status} />
            <TerraformProviderVersionVerificationStatusBadge
              verificationFailure={version.verificationFailure}
              verifiedAt={version.verifiedAt}
            />
          </Box>

          <Box align="center" gridArea="actions" gap="medium">
            <LinkButton
              size="small"
              variant="secondary"
              to={`/provider/${provider.id}/version/${version.number}/docs`}
            >
              View documentation
            </LinkButton>

            {(canPublishProviderVersion.result || canPublishProviderVersion.message) && isDraft && (
              <Tooltip
                textAlign="center"
                active={!!canPublishProviderVersion.message}
                on={(tooltipProps) => (
                  <Button
                    {...tooltipProps}
                    size="small"
                    variant="primary"
                    onPress={handlePublishProviderVersion}
                    disabled={publishMutation.loading || !canPublishProviderVersion.result}
                  >
                    Publish
                  </Button>
                )}
              >
                {canPublishProviderVersion.message}
              </Tooltip>
            )}
            {(canUpdateProviderVersion.result || canUpdateProviderVersion.message) && isRevoked && (
              <Tooltip
                active={!canUpdateProviderVersion.result}
                on={(props) => (
                  <ButtonIcon
                    {...props}
                    variant="secondary"
                    size="small"
                    icon={Edit}
                    disabled={!canUpdateProviderVersion.result}
                    onPress={() =>
                      showProviderVersionEditDrawer({
                        providerVersion: version,
                      })
                    }
                    tooltipPlacement="left"
                  >
                    Edit provider version
                  </ButtonIcon>
                )}
              >
                {canUpdateProviderVersion.message}
              </Tooltip>
            )}

            {(isPublished || isDraft) && (
              <DropdownMenuEllipsis tooltip="Version actions">
                {(canUpdateProviderVersion.result || canUpdateProviderVersion.message) && (
                  <DropdownMenuItem
                    isDisabled={!canUpdateProviderVersion.result}
                    tooltip={canUpdateProviderVersion.message}
                    isTooltipActive={!canUpdateProviderVersion.result}
                    onAction={() => {
                      showProviderVersionEditDrawer({
                        providerVersion: version,
                      });
                    }}
                  >
                    Edit
                  </DropdownMenuItem>
                )}

                <DropdownMenuItem
                  onAction={() => {
                    showProviderVersionDrawer({
                      providerVersion: version,
                      provider,
                    });
                  }}
                >
                  View instructions
                </DropdownMenuItem>

                {(canUpdateProviderVersion.result || canUpdateProviderVersion.message) &&
                  !isVerified && (
                    <DropdownMenuItem
                      isDisabled={!canUpdateProviderVersion.result || verifyMutation.loading}
                      tooltip={canUpdateProviderVersion.message}
                      isTooltipActive={!canUpdateProviderVersion.result}
                      onAction={handleVerifyProviderVersion}
                    >
                      Verify version
                    </DropdownMenuItem>
                  )}

                {(canDeleteProviderVersion.result || canDeleteProviderVersion.message) &&
                  !isPublished && (
                    <DropdownMenuItem
                      isDisabled={!canDeleteProviderVersion.result}
                      tooltip={canDeleteProviderVersion.message}
                      isTooltipActive={!canDeleteProviderVersion.result}
                      onAction={deleteConfirmationModalShow}
                      danger
                    >
                      Delete
                    </DropdownMenuItem>
                  )}

                {(canRevokeProviderVersion.result || canRevokeProviderVersion.message) &&
                  isPublished && (
                    <DropdownMenuItem
                      isDisabled={!canRevokeProviderVersion.result}
                      tooltip={canRevokeProviderVersion.message}
                      isTooltipActive={!canRevokeProviderVersion.result}
                      onAction={revokeConfirmationModalShow}
                      danger
                    >
                      Revoke
                    </DropdownMenuItem>
                  )}
              </DropdownMenuEllipsis>
            )}
          </Box>

          {version.description && (
            <Box gridArea="description" className={styles.descriptionWrapper}>
              <Markdown markup={version.description} className={styles.description} />
            </Box>
          )}
        </Box>
      </CardHeader>

      <CardCollapsibleContent hasHeader>
        <ProviderVersionArtifacts version={version} provider={provider} />
      </CardCollapsibleContent>
    </CardCollapsibleWrapper>
  );
}

export default ProviderVersion;
